import React from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { BlogApiData } from "../index";

interface InvestmentSectionProps {
  formData: BlogApiData;
  onSectionChange: (
    section: keyof BlogApiData["al_sections"],
    field: string,
    value: string
  ) => void;
  onArrayItemChange: (
    section: keyof BlogApiData["al_sections"],
    arrayField: string,
    index: number,
    field: string,
    value: string
  ) => void;
  onInvestmentImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isUploadingInvestmentImage: boolean;
  quillModules: any;
  quillFormats: string[];
}

const InvestmentSection: React.FC<InvestmentSectionProps> = ({
  formData,
  onSectionChange,
  onArrayItemChange,
  onInvestmentImageUpload,
  isUploadingInvestmentImage,
  quillModules,
  quillFormats,
}) => {
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Section Title
          </label>
          <input
            type="text"
            value={formData.al_sections.investmentSection.title}
            onChange={(e) =>
              onSectionChange("investmentSection", "title", e.target.value)
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., Investment Opportunities in Tirana"
          />
        </div>
        <div className="lg:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Section Description
          </label>
          <ReactQuill
            theme="snow"
            value={formData.al_sections.investmentSection.description}
            onChange={(value) =>
              onSectionChange("investmentSection", "description", value)
            }
            modules={quillModules}
            formats={quillFormats}
            placeholder="e.g., Why Tirana is becoming a hotspot for property investment"
          />
        </div>
      </div>
      <div className="mt-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Section Image
          </label>
          <div className="space-y-3">
            {formData.al_sections.investmentSection.image_url &&
              !isUploadingInvestmentImage && (
                <div className="flex items-center space-x-4 p-3 bg-gray-50 rounded-md">
                  <img
                    src={formData.al_sections.investmentSection.image_url}
                    alt="Investment Preview"
                    className="w-24 h-16 object-cover rounded border"
                  />
                  <div>
                    <p className="text-sm text-gray-700">Image Preview</p>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSectionChange("investmentSection", "image_url", "");
                      }}
                      className="text-xs text-red-600 hover:text-red-800">
                      Remove image
                    </button>
                  </div>
                </div>
              )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Image
              </label>
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={onInvestmentImageUpload}
                  disabled={isUploadingInvestmentImage}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                />
                {isUploadingInvestmentImage && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-md">
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      <span className="text-xs text-gray-600">
                        Uploading...
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Image Description (Alt Text)
        </label>
        <input
          type="text"
          value={formData.al_sections.investmentSection.image_description}
          onChange={(e) =>
            onSectionChange(
              "investmentSection",
              "image_description",
              e.target.value
            )
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g., Diverse skyline of Tirana business district"
        />
      </div>
      <div className="space-y-3">
        <h4 className="text-md font-medium text-gray-700">Investment Items</h4>
        {formData.al_sections.investmentSection.items.map(
          (
            item: {
              title: string;
              description: string;
            },
            index: number
          ) => (
            <div
              key={index}
              className="grid grid-cols-1 lg:grid-cols-2 gap-3 p-3 border border-gray-200 rounded-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Item {index + 1} Title
                </label>
                <input
                  type="text"
                  value={item.title}
                  onChange={(e) =>
                    onArrayItemChange(
                      "investmentSection",
                      "items",
                      index,
                      "title",
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Growing Economy"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Item {index + 1} Description
                </label>
                <input
                  type="text"
                  value={item.description}
                  onChange={(e) =>
                    onArrayItemChange(
                      "investmentSection",
                      "items",
                      index,
                      "description",
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Rapid economic development and job creation"
                />
              </div>
            </div>
          )
        )}
      </div>
      <div className="space-y-3">
        <h4 className="text-md font-medium text-gray-700">Related Sources</h4>
        {formData.al_sections.investmentSection.related_sources.map(
          (
            source: {
              title: string;
              description: string;
              link: string;
            },
            index: number
          ) => (
            <div
              key={index}
              className="grid grid-cols-1 lg:grid-cols-3 gap-3 p-3 border border-gray-200 rounded-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Source {index + 1} Title
                </label>
                <input
                  type="text"
                  value={source.title}
                  onChange={(e) =>
                    onArrayItemChange(
                      "investmentSection",
                      "related_sources",
                      index,
                      "title",
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., World Bank Report"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Source {index + 1} Description
                </label>
                <input
                  type="text"
                  value={source.description}
                  onChange={(e) =>
                    onArrayItemChange(
                      "investmentSection",
                      "related_sources",
                      index,
                      "description",
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Economic outlook for Albania"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Source {index + 1} Link
                </label>
                <input
                  type="text"
                  value={source.link}
                  onChange={(e) =>
                    onArrayItemChange(
                      "investmentSection",
                      "related_sources",
                      index,
                      "link",
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., https://example.com/report"
                />
              </div>
            </div>
          )
        )}
      </div>
    </>
  );
};

export default InvestmentSection;
